// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {Preferences} from 'mattermost-redux/constants';
import {createSelector} from 'mattermost-redux/selectors/create_selector';
import {appBarEnabled, getAppBarAppBindings} from 'mattermost-redux/selectors/entities/apps';
import {getLicense} from 'mattermost-redux/selectors/entities/general';
import {get} from 'mattermost-redux/selectors/entities/preferences';
import {createShallowSelector} from 'mattermost-redux/utils/helpers';

import type {GlobalState} from 'types/store';

export const getPluginUserSettings = createSelector(
    'getPluginUserSettings',
    (state: GlobalState) => state.plugins.userSettings,
    (settings) => {
        return settings || {};
    },
);

export const getFilesDropdownPluginMenuItems = createSelector(
    'getFilesDropdownPluginMenuItems',
    (state: GlobalState) => state.plugins.components.FilesDropdown,
    (components) => {
        return (components || []);
    },
);

export const getUserGuideDropdownPluginMenuItems = createSelector(
    'getUserGuideDropdownPluginMenuItems',
    (state: GlobalState) => state.plugins.components.UserGuideDropdown,
    (components) => {
        return components;
    },
);

export const getChannelHeaderPluginComponents = createSelector(
    'getChannelHeaderPluginComponents',
    (state: GlobalState) => appBarEnabled(state),
    (state: GlobalState) => state.plugins.components.ChannelHeaderButton,
    (state: GlobalState) => state.plugins.components.AppBar,
    (enabled, channelHeaderComponents = [], appBarComponents = []) => {
        if (!enabled || !appBarComponents.length) {
            return channelHeaderComponents;
        }

        // Remove channel header icons for plugins that have also registered an app bar component
        const appBarPluginIds = appBarComponents.map((appBarComponent) => appBarComponent.pluginId);
        return channelHeaderComponents.filter((channelHeaderComponent) => !appBarPluginIds.includes(channelHeaderComponent.pluginId));
    },
);

const getChannelHeaderMenuPluginComponentsShouldRender = createSelector(
    'getChannelHeaderMenuPluginComponentsShouldRender',
    (state: GlobalState) => state,
    (state: GlobalState) => state.plugins.components.ChannelHeader,
    (state, channelHeaderMenuComponents = []) => {
        return channelHeaderMenuComponents.map((component) => {
            if (typeof component.shouldRender === 'function') {
                return component.shouldRender(state);
            }

            return true;
        });
    },
);

export const getChannelHeaderMenuPluginComponents = createShallowSelector(
    'getChannelHeaderMenuPluginComponents',
    getChannelHeaderMenuPluginComponentsShouldRender,
    (state: GlobalState) => state.plugins.components.ChannelHeader,
    (componentShouldRender = [], channelHeaderMenuComponents = []) => {
        return channelHeaderMenuComponents.filter((component, idx) => componentShouldRender[idx]);
    },
);

export const getChannelMobileHeaderPluginButtons = createSelector(
    'getChannelMobileHeaderPluginButtons',
    (state: GlobalState) => state.plugins.components.MobileChannelHeaderButton,
    (components = []) => {
        return components;
    },
);

export const getChannelIntroPluginButtons = createSelector(
    'getChannelIntroPluginButtons',
    (state: GlobalState) => state.plugins.components.ChannelIntroButton,
    (components = []) => {
        return components;
    },
);

export const getAppBarPluginComponents = createSelector(
    'getAppBarPluginComponents',
    (state: GlobalState) => state.plugins.components.AppBar,
    (components = []) => {
        return components;
    },
);

export const getSidebarBrowseOrAddChannelMenuPluginComponents = createSelector(
    'getSidebarBrowseOrAddChannelMenuPluginComponents',
    (state: GlobalState) => state.plugins.components.SidebarBrowseOrAddChannelMenu,
    (components = []) => {
        return components;
    },
);

export const getMainMenuPluginComponents = createSelector(
    'getMainMenuPluginComponents',
    (state: GlobalState) => state.plugins.components.MainMenu,
    (components = []) => {
        return components;
    },
);

export const shouldShowAppBar = createSelector(
    'shouldShowAppBar',
    appBarEnabled,
    getAppBarAppBindings,
    getAppBarPluginComponents,
    getChannelHeaderPluginComponents,
    (enabled, bindings, appBarComponents, channelHeaderComponents) => {
        return enabled && Boolean(bindings.length || appBarComponents.length || channelHeaderComponents.length);
    },
);

export function showNewChannelWithBoardPulsatingDot(state: GlobalState): boolean {
    const pulsatingDotState = get(state, Preferences.APP_BAR, Preferences.NEW_CHANNEL_WITH_BOARD_TOUR_SHOWED, '');
    const showPulsatingDot = pulsatingDotState !== '' && JSON.parse(pulsatingDotState)[Preferences.NEW_CHANNEL_WITH_BOARD_TOUR_SHOWED] === false;
    return showPulsatingDot;
}

export const getSearchPluginSuggestions = createSelector(
    'getSearchPluginSuggestions',
    getLicense,
    (state: GlobalState) => state.plugins.components.SearchSuggestions,
    (license, components = []) => {
        if (license.IsLicensed !== 'true') {
            return [];
        }
        return components;
    },
);

export const getSearchBoxHints = createSelector(
    'getSearchBoxHints',
    getLicense,
    (state: GlobalState) => state.plugins.components.SearchHints,
    (license, components = []) => {
        if (license.IsLicensed !== 'true') {
            return [];
        }
        return components;
    },
);

export const getSearchButtons = createSelector(
    'getSearchButtons',
    getLicense,
    (state: GlobalState) => state.plugins.components.SearchButtons,
    (license, components = []) => {
        if (license.IsLicensed !== 'true') {
            return [];
        }
        return components;
    },
);

/**
 * Get a plugin's display name by its ID
 * Falls back to plugin ID if name is not available, then to 'unknown' if no plugin ID
 */
export const getPluginDisplayName = (state: GlobalState, pluginId?: string): string => {
    if (!pluginId) {
        return 'unknown';
    }
    const plugins = state.plugins?.plugins ?? {};
    return plugins[pluginId]?.name || pluginId;
};
